import React from 'react';
import { cn } from '@/utils';
import { ISortConfig, ITableColumn } from './Table.types';
import { SortIcon } from './SortIcon';

interface TableHeaderProps {
  /** Column definition */
  column: ITableColumn;
  /** Current sort configuration */
  sortConfig?: ISortConfig;
  /** Callback when header is clicked for sorting */
  onSort?: (key: string) => void;
}

/**
 * TableHeader Component
 *
 * Renders a table header cell with optional sorting functionality.
 * Matches the Figma design with proper styling and sort indicators.
 */
export const TableHeader: React.FC<TableHeaderProps> = ({ column, sortConfig, onSort }) => {
  const isActive = sortConfig?.key === column.key;
  const direction = isActive ? sortConfig.direction : undefined;

  const handleClick = () => {
    if (column.sortable && onSort) {
      onSort(column.key);
    }
  };

  const getWidth = () => {
    if (column.width) {
      return typeof column.width === 'number' ? `${column.width}px` : column.width;
    }
    return 'auto';
  };

  return (
    <th
      className={cn(
        'px-4 py-4 text-left',
        column.sortable && 'cursor-pointer text-neutral-black hover:text-brand-primary',
        column.headerClassName,
      )}
      onClick={handleClick}
      style={
        column.width ? { width: getWidth(), minWidth: getWidth() } : { minWidth: 'max-content' }
      }
    >
      <div className="pointer-events-none flex items-center gap-2">
        <span
          className={cn(
            'whitespace-nowrap text-sm font-bold leading-6',
            column.align === 'center' && 'text-center',
            column.align === 'right' && 'text-right',
          )}
        >
          {column.title}
        </span>

        {column.sortable && <SortIcon direction={direction} />}
      </div>
    </th>
  );
};

TableHeader.displayName = 'TableHeader';
